'use client'

import * as Sentry from '@sentry/nextjs'
import { useEffect, useMemo, useState } from 'react'
import { GeistSans } from 'geist/font/sans'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import Intercom from '@intercom/messenger-js-sdk'
import { openIntercomChat } from '@/features/third-party/intercom/intercom-init'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { SupportLineItem } from '@/components/support/support-line-item'
import { CardHeader, CardTitle } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { Mail, MessageCircle } from 'lucide-react'
import Image from 'next/image'
import './globals.css'
import { useCopyToClipboard } from '@/utils/hooks/use-copy-to-clipboard'
import { toast } from '@/features/toast/toast'
import { Toaster } from '@/components/ui/sonner'
import {
    getSessionStorageItem,
    removeSessionStorageItem,
    SESSION_STORAGE_KEYS,
    setSessionStorageItem,
} from '@/setup/session-storage'

function getSupportPrefillMessage(error: Error & { digest?: string }, sentryEventId?: string): string {
    const url = typeof window !== 'undefined' ? window.location.href : ''
    const msg = error.message.slice(0, 200)
    const lines = [
        'Hi, I encountered an error on the Superscale app and need help.',
        '',
        `Error: ${msg}`,
        ...(url ? [`URL: ${url}`] : []),
        ...(sentryEventId ? [`Sentry Event ID: ${sentryEventId}`] : []),
    ]
    return lines.join('\n')
}

export default function GlobalError({ error, reset }: { error: Error & { digest?: string }; reset: () => void }) {
    const [showStack, setShowStack] = useState(false)
    const { copyToClipboard } = useCopyToClipboard({
        onSuccess: () => {
            toast.success('Copied to clipboard')
        },
    })

    const [isRetrying, setIsRetrying] = useState(false)
    const [sentryEventId, setSentryEventId] = useState<string | undefined>(undefined)

    useEffect(() => {
        const appId = process.env.NEXT_PUBLIC_INTERCOM_APP_ID
        if (appId) {
            Intercom({ app_id: appId, hide_default_launcher: true })
        }
    }, [])

    const retryFailedMessage = "We couldn't recover automatically. Please reach out to our support team."

    // Case 1: reset() did nothing, same component stays mounted — detect via timeout
    useEffect(() => {
        if (!isRetrying) return
        const timeout = setTimeout(() => {
            setIsRetrying(false)
            toast.error(retryFailedMessage)
        }, 3000)
        return () => clearTimeout(timeout)
    }, [isRetrying])

    // Case 2: reset() caused a remount but error persisted — detect via session storage
    const retryFailed = useMemo(() => {
        const ts = getSessionStorageItem<string>(SESSION_STORAGE_KEYS.Error.GlobalRetryFailed)

        if (!ts) return false

        const age = Date.now() - parseInt(ts, 10)

        return age < RETRY_FAILED_MAX_AGE_MS
    }, [])

    useEffect(() => {
        const eventId = Sentry.captureException(error)
        setSentryEventId(eventId)
    }, [error])

    useEffect(() => {
        if (retryFailed) {
            removeSessionStorageItem(SESSION_STORAGE_KEYS.Error.GlobalRetryFailed)
            // Small delay to ensure Toaster is mounted after full page remount
            setTimeout(() => toast.error(retryFailedMessage), 100)
        }
    }, [retryFailed])

    const debugInfo = {
        message: error.message,
        digest: error.digest,
        stack: error.stack,
        url: typeof window !== 'undefined' ? window.location.href : undefined,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
        timestamp: new Date().toISOString(),
        sentryEventId,
    }

    const debugText = [
        `Message: ${debugInfo.message}`,
        debugInfo.digest && `Digest: ${debugInfo.digest}`,
        debugInfo.sentryEventId && `Sentry Event ID: ${debugInfo.sentryEventId}`,
        debugInfo.url && `URL: ${debugInfo.url}`,
        debugInfo.userAgent && `User Agent: ${debugInfo.userAgent}`,
        debugInfo.stack && `Stack:\n${debugInfo.stack}`,
        `Timestamp: ${debugInfo.timestamp}`,
    ]
        .filter(Boolean)
        .join('\n\n')

    return (
        <html lang="en" className={GeistSans.variable}>
            <head>
                <meta charSet="utf-8" />
                <meta name="viewport" content="width=device-width, initial-scale=1" />
                <title>Something went wrong | Superscale</title>
            </head>
            <body
                className={GeistSans.className}
                style={{
                    margin: 0,
                    minHeight: '100vh',
                    background: 'linear-gradient(180deg, #fafafa 0%, #f5f5f5 100%)',
                    color: '#171717',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 24,
                    boxSizing: 'border-box',
                }}
            >
                <Toaster />

                <div
                    style={{
                        maxWidth: 560,
                        width: '100%',
                        background: '#fff',
                        borderRadius: 12,
                        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -2px rgba(0,0,0,0.1)',
                        padding: 32,
                        border: '1px solid #e5e5e5',
                    }}
                >
                    <div style={{ marginBottom: 24 }}>
                        <div
                            style={{
                                width: 48,
                                height: 48,
                                borderRadius: 12,
                                background: '#fef2f2',
                                color: '#dc2626',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: 24,
                                marginBottom: 16,
                            }}
                        >
                            ⚠️
                        </div>
                        <h1
                            style={{
                                margin: 0,
                                fontSize: 20,
                                fontWeight: 600,
                                color: '#171717',
                                lineHeight: 1.3,
                            }}
                        >
                            Something went wrong
                        </h1>
                        <p
                            style={{
                                margin: '8px 0 0',
                                fontSize: 14,
                                color: '#737373',
                                lineHeight: 1.5,
                            }}
                        >
                            We're sorry for the inconvenience. Our team has been notified and we're on it. If you need
                            help right away, reach out to our support team—we're here for you.
                        </p>
                    </div>

                    <div
                        style={{
                            background: '#fafafa',
                            borderRadius: 8,
                            padding: 16,
                            marginBottom: 24,
                            border: '1px solid #e5e5e5',
                        }}
                    >
                        <div
                            style={{
                                fontSize: 13,
                                fontWeight: 500,
                                color: '#525252',
                                marginBottom: 4,
                            }}
                        >
                            Error message
                        </div>
                        <pre
                            style={{
                                margin: 0,
                                fontSize: 12,
                                fontFamily: 'ui-monospace, monospace',
                                color: '#171717',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                            }}
                        >
                            {error.message}
                        </pre>
                        {error.digest && (
                            <div style={{ marginTop: 8, fontSize: 11, color: '#737373' }}>
                                Digest:{' '}
                                <code style={{ background: '#f0f0f0', padding: '2px 6px', borderRadius: 4 }}>
                                    {error.digest}
                                </code>
                            </div>
                        )}
                        {error.stack && (
                            <>
                                <button
                                    type="button"
                                    onClick={() => setShowStack(!showStack)}
                                    style={{
                                        marginTop: 12,
                                        padding: 0,
                                        border: 'none',
                                        background: 'none',
                                        fontSize: 12,
                                        color: '#FF631B',
                                        cursor: 'pointer',
                                        textDecoration: 'underline',
                                    }}
                                >
                                    {showStack ? 'Hide' : 'Show'} stack trace
                                </button>
                                <div
                                    style={{
                                        maxHeight: showStack ? 400 : 0,
                                        overflow: 'hidden',
                                        transition: 'max-height 0.3s ease-out',
                                    }}
                                >
                                    <pre
                                        style={{
                                            margin: '8px 0 0',
                                            fontSize: 11,
                                            fontFamily: 'ui-monospace, monospace',
                                            color: '#525252',
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word',
                                            maxHeight: 200,
                                            overflow: 'auto',
                                        }}
                                    >
                                        {error.stack}
                                    </pre>
                                </div>
                            </>
                        )}
                    </div>

                    {retryFailed && (
                        <Alert variant="info" className="mb-3">
                            <AlertDescription>
                                We couldn't recover automatically. Please reach out to our support team—we're here to
                                help.
                            </AlertDescription>
                        </Alert>
                    )}

                    <div className="flex flex-wrap gap-2">
                        <Popover>
                            <PopoverTrigger asChild>
                                <Button variant="primary" size="sm">
                                    Contact Us
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent side="top" align="start" className="w-[320px] p-0 overflow-hidden">
                                <div className="bg-card text-card-foreground">
                                    <CardHeader className="flex flex-row items-center justify-between py-4 px-4 space-y-0">
                                        <div className="flex items-center gap-3">
                                            <div className="flex -space-x-3">
                                                <div className="relative w-8 h-8 rounded-full border-2 border-background overflow-hidden">
                                                    <Image
                                                        src="/assets/patrick.jpeg"
                                                        alt="Patrick"
                                                        fill
                                                        className="object-cover"
                                                    />
                                                </div>
                                                <div className="relative w-8 h-8 rounded-full border-2 border-background overflow-hidden">
                                                    <Image
                                                        src="/assets/selina.jpeg"
                                                        alt="Selina"
                                                        fill
                                                        className="object-cover scale-[1.5] translate-y-[4px] translate-x-[0.5px]"
                                                    />
                                                </div>
                                            </div>
                                            <div className="flex flex-col">
                                                <CardTitle className="text-lg font-semibold">
                                                    We are here for you
                                                </CardTitle>
                                                <p className="text-xs text-muted-foreground">
                                                    {"We'd love to hear from you!"}
                                                </p>
                                            </div>
                                        </div>
                                    </CardHeader>

                                    <Separator />

                                    <div className="p-3 space-y-1">
                                        <SupportLineItem
                                            icon={<MessageCircle className="h-4 w-4" />}
                                            title="Chat with us"
                                            description="Fastest response time"
                                            onClick={() => {
                                                if (typeof window !== 'undefined' && window.Intercom) {
                                                    window.Intercom(
                                                        'showNewMessage',
                                                        getSupportPrefillMessage(error, sentryEventId)
                                                    )
                                                }
                                            }}
                                        />
                                        <SupportLineItem
                                            icon={<Mail className="h-4 w-4" />}
                                            title="Email Support"
                                            description="Response within a few hours"
                                            onClick={() => {
                                                const subject = encodeURIComponent('App Error Report')
                                                const body = encodeURIComponent(
                                                    getSupportPrefillMessage(error, sentryEventId)
                                                )
                                                window.open(
                                                    `mailto:support@superscale.ai?subject=${subject}&body=${body}`
                                                )
                                            }}
                                        />
                                        <SupportLineItem
                                            icon={<MessageCircle className="h-4 w-4" />}
                                            title="Chat with us"
                                            description="Fastest response time"
                                            onClick={() => openIntercomChat()}
                                        />
                                    </div>
                                </div>
                            </PopoverContent>
                        </Popover>
                        <Button
                            variant="secondary"
                            size="sm"
                            isLoading={isRetrying}
                            onClick={() => {
                                setSessionStorageItem(SESSION_STORAGE_KEYS.Error.GlobalRetryFailed, String(Date.now()))
                                setIsRetrying(true)
                                reset()
                            }}
                        >
                            Try Again
                        </Button>
                        <Button variant="secondary" size="sm" onClick={() => copyToClipboard(debugText)}>
                            Copy Debug Information
                        </Button>
                    </div>
                </div>
            </body>
        </html>
    )
}

const RETRY_FAILED_MAX_AGE_MS = 10000
